import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Megaphone } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReleaseNotesPage() {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ version: "", title: "", description: "", show_toast: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/release-notes/current`, { withCredentials: true });
      setCurrent(r.data && r.data.id ? r.data : null);
      if (r.data && r.data.id) {
        setForm({
          version: r.data.version || "",
          title: r.data.title || "",
          description: r.data.description || "",
          show_toast: !!r.data.show_toast,
        });
      }
    } catch {/* silent */}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.version || !form.title) { toast.error("Versione e titolo sono obbligatori"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/release-notes`, form, { withCredentials: true });
      toast.success("Release note pubblicata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-release-notes-page">
      <PageHeader
        title="Release Notes"
        subtitle="Pubblica un toast in-app visibile a tutti gli utenti al primo login dopo il deploy."
      />

      <Card className="max-w-2xl border-[#0D1F3C]/8">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone size={16} className="text-[#1B4FD8]" /> Release attiva
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {current ? (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
              <strong>{current.version}</strong> · {current.title}
              <span className="text-[#0D1F3C]/40 ml-2">pubblicata il {new Date(current.created_at).toLocaleDateString("it-IT")}</span>
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-[#0D1F3C]/5 border border-[#0D1F3C]/10 text-xs text-[#0D1F3C]/60">
              Nessuna release note attiva.
            </div>
          )}
          <div>
            <Label className="text-xs mb-1 block">Versione *</Label>
            <Input value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))} placeholder="es. v2026.04.27" data-testid="rn-version" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Titolo *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Pre-go-live bundle" data-testid="rn-title" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Descrizione</Label>
            <Textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Riepilogo cambiamenti..." data-testid="rn-description" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.show_toast} onCheckedChange={v => setForm(p => ({ ...p, show_toast: v }))} data-testid="rn-show-toast" id="rn-show-toast" />
            <Label htmlFor="rn-show-toast" className="text-xs cursor-pointer">Mostra toast a tutti gli utenti al prossimo login</Label>
          </div>
          <Button onClick={save} disabled={busy} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="rn-save-btn">
            {busy ? "Pubblicazione…" : "Pubblica release note"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
